import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getApplicationDetails, ApplicationDetailsDto } from "../api/services";
import { ApplicationItem, StepType } from "../types";
import { ApplicationCard } from "../components/application/ApplicationCard";

const Application: React.FC = () => {
  const { t } = useTranslation();
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [items, setItems] = useState<ApplicationItem[]>([]);
  const [propertyName, setPropertyName] = useState("");
  const [unitName, setUnitName] = useState("");
  const [moveInDate, setMoveInDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!applicationId) return;

    setLoading(true);
    getApplicationDetails(applicationId)
      .then((res) => {
        const app: ApplicationDetailsDto = res.data;

        // Redirect to payment if payment is required by tenant and not paid
        if (app.payment === "tenant" && app.paymentStatus !== "paid") {
          navigate(`/application/${applicationId}/payment`);
          return;
        }

        setPropertyName(app.unit.property.name);
        setUnitName(app.unit.name);
        setMoveInDate(new Date(app.createdAt).toLocaleDateString());

        const mapped: ApplicationItem[] = app.items.map((it) => ({
          id: it.type,
          type: it.type as StepType,
          status: it.status,
          data: it.data,
          title: t(`application.item.${it.type}Title`),
          description: t(`application.item.${it.type}Description`),
        }));

        setItems(mapped);
        setErrorKey(null);
      })
      .catch((err) => {
        console.error(err);
        setErrorKey("application.errorLoadingDetails");
      })
      .finally(() => setLoading(false));
  }, [applicationId, t]);

  if (loading) {
    return <p>{t("application.loading", "Loading...")}</p>;
  }
  if (errorKey) {
    return <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">{t(errorKey)}</div>;
  }

  const firstIncomplete = items.find((i) => i.status !== "completed");

  return (
    <div className="application-screen">
      <h1 className="mb-6">{t("application.title")}</h1>

      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="flex items-center justify-between p-5">
          <div>
            <strong>{propertyName}</strong>
            <br />
            {unitName}
          </div>
          <div className="text-right">
            {t("application.moveInDate")}
            <br />
            <strong>{moveInDate}</strong>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {items.map((item) => (
          <ApplicationCard
            key={item.id}
            applicationId={applicationId!}
            item={item}
            highlight={item.id === firstIncomplete?.id}
          />
        ))}
      </div>

      <div className="mt-6 text-center">
        {firstIncomplete ? (
          <button
            className="rounded-lg bg-info px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sky-600"
            onClick={() =>
              navigate(`/application/${applicationId}/${firstIncomplete.id}`)
            }
          >
            {t("application.continueWith", {
              step: t(`application.item.${firstIncomplete.id}Title`),
            })}
          </button>
        ) : (
          <p className="text-lg">
            <strong>{t("application.submittedMessage")}</strong>
          </p>
        )}
      </div>
    </div>
  );
};

export default Application;
